<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
  >
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>
    <div
      class="kds-main-wrapper"
      data-qa="prescription-upload"
    >
      <div class="kds-content-block">
        <p
          class="kds-typography-title-xlarge"
          v-html="header"
        />
        <p
          class="kds-typography-paragraph-large kds-typography-normal-white-space"
          v-html="description"
        />
      </div>
      <FileSelection
        ref="fileSelectionRef"
        :selection-choose-ctrl-text="
          $t('eu__prescription_upload__file_selection')
        "
        :selection-preview="true"
        data-qa="file-selection"
        selection-hint="(PNG, JPG, PDF | max. 10MB)"
        @reset="handleFileReset"
        @select="handleFileSelection"
      />
      <BaseBanner
        v-if="uploadError"
        scroll-into-view
        :title="$t('eu__prescription_upload__error__header')"
        variant="warning"
      >
        <p v-html="$t('eu__prescription_upload__error__description')" />
      </BaseBanner>
    </div>
    <PrescriptionConsentSheet
      ref="confirmationModalRef"
      :description="consentDescription"
      @confirm="handleConfirmationSubmit"
      @cancel="handleConfirmationCancel"
    />
    <template #actions>
      <BaseButton
        :disabled="submitButtonState === 'DISABLED'"
        :loading="submitButtonState === 'PROCESSING'"
        :success="submitButtonState === 'SUCCESS'"
        :text="$t('eu__prescription_upload__primary_cta')"
        data-qa="submit"
        @click="handleSubmitEvent"
      />
      <BaseButton
        :disabled="cancelButtonState === 'DISABLED'"
        :text="$t('eu__prescription_upload__secondary_cta')"
        data-qa="cancel"
        variant="outlined"
        @click="handleCancelEvent"
      />
    </template>
  </ResponsiveLayout>
  <PrescriptionSupportModal
    ref="supportModalRef"
    show-skip
    @skip="navigateNext('supportSkipped')"
  />
</template>

<script setup>
import { computed, ref } from 'vue'
import useUserStore from '@shared/stores/useUserStore.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import { escapeHtml } from '@shared/utils/escapeHtml'
import { uploadPrescriptionFile } from '@shared/api-endpoints'
import { wait } from '@shared/utils.js'
import { useI18n } from 'petite-vue-i18n'
import env from '@shared/env'
import BaseBanner from '@shared/components/BaseBanner.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import FileSelection from '@eu/app/components/FileSelection.vue'
import Tracker from '@shared/Tracker'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import * as apiRequest from '@shared/api-endpoints/index.js'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import useNavigation from '@eu/app/composables/useNavigation'
import ROUTE from '@eu/app/router/appModule/names'
import PrescriptionConsentSheet from '@eu/app/components/PrescriptionConsentSheet.vue'
import PrescriptionSupportModal from '@eu/app/components/PrescriptionSupportModal.vue'

const { t } = useI18n()
const userStore = useUserStore()
const corporateStore = useCorporateStore()
const { progress } = useOnboardingProgress()
const { navigateNext, navigateBack, canGoBack } = useNavigation(
  ROUTE.PRESCRIPTION_UPLOAD,
)

const submitButtonState = ref('DISABLED')
const cancelButtonState = ref('ACTIVE')
const confirmationModalRef = ref(null)
const selectedFile = ref(null)
const uploadError = ref(false)
const fileSelectionRef = ref(null)
const supportModalRef = ref(null)

const user = computed(() => userStore.user)
const corporate = computed(() => corporateStore.corporate)
const appName = computed(() => t('eu__generic__app'))

const header = computed(() => t('eu__prescription_upload__header'))
const description = computed(() =>
  !env.features.diga
    ? t('eu__prescription_upload__description__diga_disabled')
    : t('eu__prescription_upload__description', {
        app_name: appName.value,
        corporate: escapeHtml(corporate.value.title),
      }),
)
// The sheet builds the standard text itself
const consentDescription = computed(() =>
  env.features.diga
    ? null
    : t('eu__prescription_upload__confirmation__description__diga_disabled', {
        app_name: appName.value,
      }),
)

const hasPrescriptionProcessingConsent = computed(
  () => user.value?.consents?.prescription_file_processing_agreement === true,
)

const handleFileSelection = (files) => {
  submitButtonState.value = 'ACTIVE'
  selectedFile.value = files[0]
  uploadError.value = false
}

const handleFileReset = () => {
  submitButtonState.value = 'DISABLED'
  selectedFile.value = null
  uploadError.value = false
}

const handleSubmitEvent = () => {
  Tracker.trackButtonSelect(
    'prescription_upload_send',
    'signup_prescription_upload',
  )
  if (!hasPrescriptionProcessingConsent.value) {
    Tracker.trackViewScreen('signup_prescription_upload_consent')
    confirmationModalRef.value.open()
  } else {
    handleConfirmationSubmit()
  }
}

const handleCancelEvent = () => {
  Tracker.trackButtonSelect('manual_prescription', 'signup_prescription_upload')
  apiRequest.sendSubscriptionActivationEntry({
    type: 'intends_to_self_submit_prescription',
  })
  if (env.features.diga) {
    navigateNext('selfSubmit')
  } else {
    supportModalRef.value.open()
  }
}

const handleConfirmationSubmit = () => {
  if (selectedFile.value == null) {
    return
  }
  confirmationModalRef.value.close()
  submitButtonState.value = 'PROCESSING'
  cancelButtonState.value = 'DISABLED'
  fileSelectionRef.value.nextCtrlState({
    state: 'DISABLED',
  })
  const formData = new FormData()
  formData.append('corporate_id', corporate.value.id)
  formData.append('file', selectedFile.value)
  if (!hasPrescriptionProcessingConsent.value) {
    Tracker.trackButtonSelect(
      'prescription_upload_agree',
      'signup_prescription_upload_consent',
    )
  }
  // consent is guaranteed here: either stored on the user or just given via the modal
  formData.append('accepted_file_processing_agreement', 'true')
  uploadPrescriptionFile(formData)
    .then(async () => {
      Tracker.trackKaiaEvent(
        {
          event_name: 'c.lfcycl.upload_subscription',
          action: 'view',
          object_type: 'screen',
          screen_name: 'signup_prescription_upload',
        },
        {
          prescription_upload_status: 'success',
        },
      )
      submitButtonState.value = 'SUCCESS'
      await wait(2000)
      await navigateNext('uploaded')
    })
    .catch(async (e) => {
      const error = e.response?.data?.error || e.message
      submitButtonState.value = 'ACTIVE'
      cancelButtonState.value = 'ACTIVE'
      fileSelectionRef.value.nextCtrlState({
        state: 'DEFAULT',
      })
      uploadError.value = true
      Tracker.trackKaiaEvent(
        {
          event_name: 'c.lfcycl.upload_subscription',
          action: 'view',
          object_type: 'screen',
          screen_name: 'signup_prescription_upload',
        },
        {
          error_message: error,
          prescription_upload_status: 'failed',
        },
      )
    })
}

const handleConfirmationCancel = () => {
  Tracker.trackButtonSelect(
    'prescription_upload_cancel',
    'signup_prescription_upload_consent',
  )
  confirmationModalRef.value.close()
}
</script>
